import moment from 'moment'
import {useEffect, useRef, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {PatientAcknowledgement} from 'src/components-new/feedback/activityAckUtils'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {CalendarEvent} from './agendaUtils'
import CalendarNew from './CalendarNew'

// ---------------------------------------------------------------------------
// captureStatus helper — apiClientRequest never re-throws on error, so we
// intercept the status setter to detect REJECTED and trigger side-effects.
// Each concurrent fetch must receive its own instance so a FULFILLED from one
// call cannot overwrite a REJECTED from the other.
// ---------------------------------------------------------------------------

const captureStatus = (onRejected: () => void) =>
  (status: string) => {
    if (status === STATUS.REJECTED) onRejected()
  }

// The visible range of the calendar for a date: whole weeks around its month, as the calendar's
// own navigation (CalendarNew.handleDateChange) sets it.
const rangeStart = (date: any) => moment(date).startOf('month').startOf('week')
const rangeEnd = (date: any) => moment(date).endOf('month').endOf('week')

// An activity to open, from a web push notification (public/service-worker.js):
// /agenda?activityId=…&date=<ISO planned date>
type ActivityToOpen = {activityId: string; date?: Date}

function readActivityToOpen(search: string): ActivityToOpen | undefined {
  const params = new URLSearchParams(search)
  const activityId = params.get('activityId')
  if (!activityId) return undefined
  const date = params.get('date') ? new Date(params.get('date') as string) : undefined
  return {activityId, date: date && !Number.isNaN(date.getTime()) ? date : undefined}
}

// ---------------------------------------------------------------------------
// Page container
// ---------------------------------------------------------------------------

const AgendaNew = (): JSX.Element => {
  const toast = useToast()
  // Stable ref so effects/callbacks always call the current toast without
  // needing to include `toast` in dependency arrays.
  const toastRef = useRef(toast)
  toastRef.current = toast

  const location = useLocation()
  const history = useHistory()

  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [eventsTypes, setEventsTypes] = useState<Record<string, any>[]>([])
  const [start, setStart] = useState<any>(() => rangeStart(readActivityToOpen(location.search)?.date ?? moment()))
  const [end, setEnd] = useState<any>(() => rangeEnd(readActivityToOpen(location.search)?.date ?? moment()))
  // The range `events` were loaded for: an activity to open is looked for only once the events of
  // the range it is in have arrived.
  const [loadedRange, setLoadedRange] = useState<{start: any; end: any}>()
  const [activityToOpen, setActivityToOpen] = useState<ActivityToOpen>()
  const [openedActivity, setOpenedActivity] = useState<CalendarEvent>()

  // -------------------------------------------------------------------------
  // Data fetching — re-runs whenever the visible date range changes.
  // Each fetch gets its own captureStatus instance to avoid cross-fetch
  // FULFILLED overwriting a REJECTED from a sibling call.
  // -------------------------------------------------------------------------

  useEffect(() => {
    const fetchEvents = async () => {
      const fetched = await apiClient.getUserEvents(
        start, end,
        captureStatus(() => toastRef.current.error("Errore nel recuperare gli eventi dell'agenda."))
      ) ?? []
      setEvents(fetched as CalendarEvent[])
      setLoadedRange({start, end})
    }

    const fetchEventsTypes = async () => {
      const fetched = await apiClient.getUserEventsTypes(
        captureStatus(() => toastRef.current.error('Errore nel recuperare i tipi di eventi.'))
      ) ?? []
      setEventsTypes(fetched as Record<string, any>[])
    }

    fetchEvents()
    fetchEventsTypes()
  }, [start, end])

  // -------------------------------------------------------------------------
  // Activity to open from a web push notification: moves the calendar to the month of its date,
  // then opens its detail once that month is loaded. The query string is dropped afterwards, so a
  // reload or a back navigation does not open it again.
  // -------------------------------------------------------------------------

  useEffect(() => {
    const toOpen = readActivityToOpen(location.search)
    if (!toOpen) return
    if (toOpen.date) {
      const newStart = rangeStart(toOpen.date)
      setStart((current: any) => (current.isSame(newStart) ? current : newStart))
      setEnd((current: any) => (current.isSame(rangeEnd(toOpen.date)) ? current : rangeEnd(toOpen.date)))
    }
    setActivityToOpen(toOpen)
  }, [location.search])

  useEffect(() => {
    if (!activityToOpen || loadedRange?.start !== start || loadedRange?.end !== end) return
    const activity = events.find((event) => event._id === activityToOpen.activityId)
    if (activity) setOpenedActivity(activity)
    setActivityToOpen(undefined)
    history.replace(location.pathname)
  }, [activityToOpen, events, loadedRange, start, end, history, location.pathname])

  // -------------------------------------------------------------------------
  // Save / update callbacks — mirror legacy Agenda.tsx logic exactly
  // -------------------------------------------------------------------------

  const onSave = async (values: CalendarEvent) => {
    const {warning, ...rest} = values as any

    const event = {
      ...rest,
      user: rest.user?._id ?? rest.user,
      channels: rest.channels ?? [],
      reminderMilliseconds: warning ? warning.value * warning.unit : undefined,
    }

    await apiClient.postAppointment(event, (status: string) => {
      if (status === STATUS.REJECTED) {
        toastRef.current.error("Errore nel salvare l'appuntamento.")
      }
    })

    // Refetch to reflect the newly created event
    const fetched = await apiClient.getUserEvents(
      start, end,
      captureStatus(() => toastRef.current.error('Errore nel ricaricare gli eventi.'))
    ) ?? []
    setEvents(fetched as CalendarEvent[])
  }

  const onUpdate = async (values: CalendarEvent, id: string | undefined) => {
    const {warning, ...rest} = values as any

    const event = {
      ...rest,
      user: rest.user?._id ?? rest.user,
      channels: rest.channels ?? [],
      reminderMilliseconds: warning ? warning.value * warning.unit : undefined,
    }

    await apiClient.patchAppointment({event, id}, (status: string) => {
      if (status === STATUS.REJECTED) {
        toastRef.current.error("Errore nell'aggiornare l'appuntamento.")
      }
    })

    // Refetch to reflect the updated event
    const fetched = await apiClient.getUserEvents(
      start, end,
      captureStatus(() => toastRef.current.error('Errore nel ricaricare gli eventi.'))
    ) ?? []
    setEvents(fetched as CalendarEvent[])
  }

  // Local update (no refetch): the backend only flags the acknowledgement
  // on the activity, nothing else in the visible range changes.
  const onActivityAcknowledged = (activityId: string, acknowledgement: PatientAcknowledgement) => {
    setEvents((current) => current.map((event) => (
      event._id === activityId ? {...event, patientAcknowledgement: acknowledgement} : event
    )))
  }

  // -------------------------------------------------------------------------
  // Render — no LayoutContainer or PageHeader; AppShellNew provides the chrome
  // -------------------------------------------------------------------------

  return (
    <CalendarNew
      events={events}
      eventsTypes={eventsTypes}
      onSave={onSave}
      onUpdate={onUpdate}
      onActivityAcknowledged={onActivityAcknowledged}
      openedActivity={openedActivity}
      startDate={start}
      setStartDate={setStart}
      endDate={end}
      setEndDate={setEnd}
    />
  )
}

export default AgendaNew
